import { identity, ApiError, unwrap } from "./api";
import { permissionForPath } from "./nav";
import { parsePermissions } from "./permissions";
import { disablePush } from "./push";
import type { AccountSummary, PermissionState } from "../types";

/**
 * Ported from apps/admin/lib/session.ts + the (auth) actions.ts, through the generated identity
 * client (FR-006) rather than a hand-rolled fetch. One simplification the BFF buys for free: the
 * old parseSessionTokens() guard existed because the Next.js server had to read
 * accessToken/refreshToken out of Identity's JSON body itself. Here the BFF does that and hands
 * the SPA back `{ ok: true }` — the token pair never reaches this file, so there's nothing left
 * to parse (contracts/bff-contract.md).
 */

type RefusalOutcome = { refused: number | null };

/** The Turnstile token (src/components/turnstile.tsx) as the header the BFF verifies (server/turnstile.js). */
const captcha = (token: string) => ({ "cf-turnstile-response": token });

function isRefusal(status: number): boolean {
  return status === 401 || status === 429;
}

export async function signIn(email: string, password: string, captchaToken: string): Promise<RefusalOutcome> {
  try {
    await unwrap(
      (await identity()).POST("/api/identity/sign-in", { body: { email, password }, headers: captcha(captchaToken) }),
    );
  } catch (err) {
    if (err instanceof ApiError && isRefusal(err.status)) return { refused: err.status };
    throw err;
  }
  return { refused: null };
}

/**
 * Email only. Staff accounts are invitation-made and email-keyed; Identity has no staff mobile to
 * match, so a mobile's first code would create a stray retail_customer the BFF then refuses (403).
 * ponytail: back to "mobile or email" once staff accounts carry a mobile.
 */
function otpContact(email: string): { mobile: null; email: string } {
  return { mobile: null, email: email.trim() };
}

/**
 * Throws every failure: a 404/403 says why this email gets no code, and a 429 carries the real
 * wait (`resendAvailableInSeconds`) — swallowing either would tell staff a code is on its way.
 */
export async function requestOtp(email: string, captchaToken: string): Promise<void> {
  await unwrap(
    (await identity()).POST("/api/identity/otp/request", { body: otpContact(email), headers: captcha(captchaToken) }),
  );
}

export async function verifyOtp(email: string, code: string): Promise<RefusalOutcome> {
  try {
    await unwrap(
      (await identity()).POST("/api/identity/otp/verify", { body: { ...otpContact(email), code: code.trim() } }),
    );
  } catch (err) {
    if (err instanceof ApiError && isRefusal(err.status)) return { refused: err.status };
    throw err;
  }
  return { refused: null };
}

export async function previewInvitation(
  token: string,
): Promise<{ data: unknown; error: null } | { data: null; error: ApiError }> {
  try {
    return {
      data: await unwrap((await identity()).POST("/api/identity/invitations/preview", { body: { token } })),
      error: null,
    };
  } catch (err) {
    if (err instanceof ApiError) return { data: null, error: err };
    throw err;
  }
}

/** `password` is left out for a passwordless role — the preview's `passwordRequired: false`. */
export async function acceptInvitation(
  token: string,
  displayName: string,
  password: string | undefined,
  captchaToken: string,
): Promise<void> {
  await unwrap(
    (await identity()).POST("/api/identity/invitations/accept", {
      body: { token, displayName, password },
      headers: captcha(captchaToken),
    }),
  );
}

/**
 * Whether accepting this invitation asks for a password. Identity's preview says
 * `passwordRequired: false` for a passwordless role (customers and warehouse staff, who sign in by
 * one-time code); anything else — including a preview from before that field existed — asks.
 */
export function invitationNeedsPassword(preview: unknown): boolean {
  return (preview as { passwordRequired?: unknown } | null)?.passwordRequired !== false;
}

/** Same wording whether or not the account exists — an outcome-branched screen would be a disclosure. */
export async function requestPasswordReset(email: string, captchaToken: string): Promise<void> {
  try {
    await unwrap(
      (await identity()).POST("/api/identity/password-reset/request", { body: { email }, headers: captcha(captchaToken) }),
    );
  } catch (err) {
    if (!(err instanceof ApiError && isRefusal(err.status))) throw err;
  }
}

export async function completePasswordReset(token: string, newPassword: string): Promise<RefusalOutcome> {
  try {
    await unwrap(
      (await identity()).POST("/api/identity/password-reset/complete", { body: { token, newPassword } }),
    );
  } catch (err) {
    if (err instanceof ApiError && (isRefusal(err.status) || err.status === 400 || err.status === 410)) {
      return { refused: err.status };
    }
    throw err;
  }
  return { refused: null };
}

/**
 * Tells the gateway (revokes server-side, via the BFF), then always clears cookies — the BFF's
 * job, ported — and always leaves the shell, same as apps/portal's signOut action. Without the
 * navigation, the SPA's in-memory loader data outlives the now-cleared cookies: the dashboard
 * stays rendered as if still signed in until the next full navigation.
 */
export async function signOut(): Promise<void> {
  await disablePush();
  await endSession();
  window.location.assign("/sign-in");
}

/** Revokes the session and clears its cookies (the BFF does both whatever the gateway answers), without navigating. */
export async function endSession(): Promise<void> {
  await unwrap((await identity()).POST("/api/identity/sign-out")).catch(() => {});
}

/**
 * A runtime guard, not a hand-written contract (backend-gaps GAP-4) — Identity declares no
 * response body, so the fields are asserted rather than assumed.
 */
export function parseAccount(body: unknown): AccountSummary {
  const raw = (body ?? {}) as Record<string, unknown>;
  const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

  const id = text(raw.id);
  const roleCode = text(raw.role);
  if (id === null || roleCode === null) {
    throw new Error(
      "GET /api/identity/me answered without a string id and role. The shell renders the " +
        "signed-in account (FR-012) and has nothing to render without them.",
    );
  }

  return {
    id,
    role: roleCode,
    displayName: text(raw.displayName),
    companyRef: text(raw.companyRef),
    companyName: text(raw.companyName),
    email: text(raw.email),
    // Only an explicit `false` hides "change password": an Identity from before the field still shows it.
    hasPassword: raw.hasPassword !== false,
  };
}

/** The signed-in account, or null when this browser has no usable session. */
export async function getAccount(): Promise<AccountSummary | null> {
  try {
    return parseAccount(await unwrap((await identity()).GET("/api/identity/me")));
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function getPermissionState(): Promise<PermissionState> {
  return parsePermissions(await unwrap((await identity()).GET("/api/identity/me/permissions")));
}

/**
 * Where a fresh sign-in lands. `returnTo` was recorded from whatever session was open before, often
 * another account's (a Super Admin's tab expires on Users, then Sales signs in), so a screen this
 * account cannot open sends it to the Dashboard rather than straight onto a "Not permitted" panel.
 */
export function landingFor(returnTo: string, permissionState: PermissionState): string {
  const required = permissionForPath(new URL(returnTo, "https://admin.invalid").pathname);
  return required === undefined || permissionState.permissions.has(required) ? returnTo : "/";
}

/** `landingFor` against the just-signed-in session; an unreadable permission set keeps `returnTo` (the shell gates it anyway). */
export async function landingAfterSignIn(returnTo: string): Promise<string> {
  return getPermissionState().then((state) => landingFor(returnTo, state), () => returnTo);
}

/** One live session of the caller's (MeQueries.MySessionView). No token/hash reaches this app — Identity never sends one. */
export type MySession = {
  id: string;
  client: string | null;
  sourceIp: string | null;
  lastUsedAt: string;
  current: boolean;
};

/** GAP-4 again: `GET /me/sessions` declares no response body, so the shape is asserted at runtime, not assumed. */
function parseSessions(body: unknown): MySession[] {
  const raw = (body ?? {}) as Record<string, unknown>;
  const items = Array.isArray(raw.items) ? raw.items : [];

  return items.map((item) => {
    const row = (item ?? {}) as Record<string, unknown>;
    const id = typeof row.id === "string" ? row.id : null;
    const lastUsedAt = typeof row.lastUsedAt === "string" ? row.lastUsedAt : null;
    if (id === null || lastUsedAt === null) {
      throw new Error("GET /api/identity/me/sessions answered an item without a string id and lastUsedAt.");
    }
    return {
      id,
      client: typeof row.client === "string" ? row.client : null,
      sourceIp: typeof row.sourceIp === "string" ? row.sourceIp : null,
      lastUsedAt,
      current: row.current === true,
    };
  });
}

/** FR-048: every live session of the signed-in account, current one flagged. */
export async function listMySessions(): Promise<MySession[]> {
  return parseSessions(await unwrap((await identity()).GET("/api/identity/me/sessions")));
}

/** 404s for a session id that isn't the caller's own — see MeController's own remark on that. */
export async function revokeMySession(id: string): Promise<void> {
  await unwrap((await identity()).DELETE("/api/identity/me/sessions/{id}", { params: { path: { id } } }));
}

/**
 * GAP-4 again: `GET /me/grantable-roles` declares no response body, so the shape is asserted at
 * runtime, not assumed. The list is a hint for the dropdown only — `changeUserRole`/
 * `createInvitation` still independently enforce via the same matrix check server-side.
 */
function parseGrantableRoles(body: unknown): string[] {
  const raw = (body ?? {}) as Record<string, unknown>;
  const roles = Array.isArray(raw.roles) ? raw.roles : [];
  return roles.filter((role): role is string => typeof role === "string");
}

/**
 * Every role the signed-in caller's own role may grant (`IGrantableRoles.MayGrant`), for the
 * change-role and invite dropdowns.
 */
export async function listGrantableRoles(): Promise<string[]> {
  return parseGrantableRoles(await unwrap((await identity()).GET("/api/identity/me/grantable-roles")));
}

/**
 * Only a path on this origin — a returnTo is attacker-supplied by definition (query string).
 * Resolved the way a browser resolves it, not by its first two characters: a backslash, or a
 * tab/newline after the first slash, both become `//evil.example` in a browser (same fix as the portal's T124).
 */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  const base = "https://admin.invalid";
  if (new URL(value, base).origin !== base) return "/";
  return value;
}
